import React, { useEffect, useState } from 'react';

import { ReactComponent as InfoIcon } from 'assets/icons/ic_info.svg';
import Button from 'components/button/Button';
import FixedLoading from 'components/common/FixedLoading';
import InputContainer from 'components/input/InputContainer';
import ContentModalHeader from 'components/modal/ContentModalHeader';
import { usePostEmailCertification } from 'features/auth';
import { useTranslation } from 'react-i18next';
import { isValidEmail, removeWhitespace } from 'shared/lib';

import { sanitizeEnglishName, trackSignupFieldError } from '../../lib';
import GoogleSignInButton from '../GoogleSignInButton';
import SignupBenefitLine from './SignupBenefitLine';

interface SignupStep1Props {
  form: {
    firstName: string;
    lastName: string;
    email: string;
  };
  onInputChange: (field: string, value: string) => void;
  onClickNext: () => void;
}

const SignupStep1 = ({ onClickNext, form, onInputChange }: SignupStep1Props) => {
  const { t } = useTranslation('auth');
  const [firstNameError, setFirstNameError] = useState('');
  const [lastNameError, setLastNameError] = useState('');
  const [emailError, setEmailError] = useState('');

  const { mutateAsync: trySendEmailCertification, isPending } = usePostEmailCertification({
    onSuccess: () => {
      window.dataLayer?.push({
        event: 'sign_up_step1',
      });

      onClickNext();
    },
    onError: (error) => {
      if (error === '중복된 이메일입니다.') {
        trackSignupFieldError(1, 'email_exists');
        alert(t('signup.step1.accountExistsError'));
      } else {
        alert(error);
      }
    },
  });

  const handleNameChange = (field: string, value: string) => {
    setFirstNameError('');
    setLastNameError('');

    const sanitizedName = sanitizeEnglishName(value);
    if (sanitizedName === null) return;

    onInputChange(field, sanitizedName);
  };

  const handleEmailChange = (field: string, value: string) => {
    setEmailError('');

    onInputChange(field, removeWhitespace(value));
  };

  const handleNext = async () => {
    if (form.firstName === '') {
      setFirstNameError(t('signup.step1.firstNameRequired'));
      trackSignupFieldError(1, 'first_name');
      return;
    }

    if (form.lastName === '') {
      setLastNameError(t('signup.step1.lastNameRequired'));
      trackSignupFieldError(1, 'last_name');
      return;
    }

    if (form.email === '') {
      setEmailError(t('signup.step1.emailRequired'));
      trackSignupFieldError(1, 'email');
      return;
    }

    if (!isValidEmail(form.email)) {
      setEmailError(t('signup.step1.validEmailRequired'));
      trackSignupFieldError(1, 'email_invalid');
      return;
    }

    try {
      await trySendEmailCertification(form.email);
    } catch (error) {
      console.error(error);
    }
  };

  const onKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleNext();
    }
  };

  useEffect(() => {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'begin_sign_up',
      });
    }
  }, []);

  return (
    <div className="flex w-[309px] max-w-full flex-col gap-y-4 px-5 py-5 md:mx-45 md:my-6 md:gap-y-5 md:p-0">
      <div className="flex w-full flex-col gap-y-4">
        <ContentModalHeader
          src="/images/optimized/illusts/il_key.webp"
          title={t('signup.title')}
          description={t('signup.step1.description')}
        />
        <SignupBenefitLine className="-mt-2" />
        <GoogleSignInButton />
        <div className="flex w-full flex-col gap-y-px">
          <InputContainer
            label={t('signup.step1.firstNameLabel')}
            placeholder={t('signup.step1.firstNamePlaceholder')}
            error={firstNameError}
            value={form.firstName}
            onChange={(e) => handleNameChange('firstName', e.target.value)}
          />
          <InputContainer
            label={t('signup.step1.lastNameLabel')}
            placeholder={t('signup.step1.lastNamePlaceholder')}
            error={lastNameError}
            value={form.lastName}
            onChange={(e) => handleNameChange('lastName', e.target.value)}
          />
          <InputContainer
            label={t('signup.step1.emailLabel')}
            placeholder={t('signup.step1.emailPlaceholder')}
            error={emailError}
            value={form.email}
            onKeyPress={onKeyPress}
            onChange={(e) => handleEmailChange('email', e.target.value)}
          />
        </div>
        <div className="-mt-4 flex w-full gap-x-1.5 pt-px text-c2 text-primary">
          <InfoIcon className="shrink-0" width={21} height={21} />
          <p className="flex-1 text-wrap">{t('signup.step1.passportNote')}</p>
        </div>
      </div>
      <Button text={t('signup.step1.continue')} shape="SQUARE" onClick={handleNext} disabled={isPending} />
      {isPending && <FixedLoading />}
    </div>
  );
};

export default SignupStep1;
